import type { CroppingInfo } from '@/components/ImageField.vue';
import type { Locales, ThemeHost } from '@/services/types/api.types';
import { useConfigurationStore } from '@/stores';
import { ModuleTypeEnum } from '@/types/modules';
import { computed, unref, type ComputedRef, type MaybeRef } from 'vue';
import { useI18n } from 'vue-i18n';

export interface LayoutOption {
  value: number;
  label: string;
  description?: string;
}

// `host` is the kind of parent the blocks sit on: a theme offers each host its own block types.
export const useTheme = (host: MaybeRef<ThemeHost>, themeKey: MaybeRef<string>) => {
  const { locale } = useI18n();
  const configurationStore = useConfigurationStore();
  const themes = computed(() => configurationStore.getThemes(unref(host)));
  const theme = computed(
    () =>
      themes.value.find((theme) => theme.key === unref(themeKey)) ??
      themes.value.find((theme) => theme.key === 'default'),
  );
  const offerOf = (moduleType: ModuleTypeEnum) => theme.value?.hosts[unref(host)]?.offers[moduleType];

  const getLayoutOptions = (moduleType: ModuleTypeEnum): ComputedRef<LayoutOption[]> => {
    return computed(() => {
      const offer = offerOf(moduleType);
      if (!offer) return [];

      const resolvedLocale = locale.value as Locales;

      return offer.variants.map((layout, index) => ({
        value: index + 1,
        label: layout.title?.[resolvedLocale] ?? '',
        description: layout.subtitle?.[resolvedLocale] ?? '',
      }));
    });
  };

  const getCroppingInfo = (moduleType: ModuleTypeEnum, layoutIndex: MaybeRef<number>): ComputedRef<CroppingInfo> => {
    return computed(() => {
      const resolvedLayoutIndex = unref(layoutIndex);
      if (resolvedLayoutIndex <= 0) throw new Error('Layout index should be greater than 0');

      const offer = offerOf(moduleType);
      if (!offer) return { aspectRatio: 1, size: { width: 0, height: 0 } };

      const layoutConfig = offer.variants[resolvedLayoutIndex - 1];
      if (!layoutConfig || !layoutConfig.cropping) return { aspectRatio: 1, size: { width: 0, height: 0 } };

      return layoutConfig.cropping;
    });
  };

  // Whether the theme offers this block type on the host: the type picker shows only those.
  const isOffered = (moduleType: ModuleTypeEnum): boolean => Boolean(offerOf(moduleType));

  return { getCroppingInfo, getLayoutOptions, isOffered };
};
